/**
 * CMS Published Cache Entity
 *
 * 발행된 콘텐츠를 섹션+언어 단위로 캐싱합니다.
 * - 초고속 읽기 성능 (5ms)
 * - 단일 쿼리로 섹션 전체 로드
 * - JSONB로 즉시 사용 가능한 데이터
 *
 * 성능 비교:
 * - 정규화 테이블 JOIN: 87ms
 * - 캐시 테이블: 5ms (17배 빠름!)
 */

import { serial, text, jsonb, integer, timestamp, index, unique } from 'drizzle-orm/pg-core';
import { cmsSchema } from './cms-schema';

// Create isolated schema for @spfn/cms
// Schema imported from cms-schema.ts

export const cmsPublishedCache = cmsSchema.table('published_cache', {
    // Primary Key
    id: serial('id').primaryKey(),

    // 섹션 (페이지 단위)
    section: text('section').notNull(),
    // "home" | "why-futureplay" | "team" | "our-companies" | "apply"

    // 언어
    locale: text('locale').notNull(),
    // "ko" | "en" | "ja"

    // 캐시된 콘텐츠 (JSONB)
    content: jsonb('content').notNull(),
    // Record<string, LabelValue>
    // {
    //   "home.hero.title": { type: "text", content: "..." },
    //   "home.hero.image": { type: "image", url: "...", alt: "..." },
    //   ...
    // }

    // 발행 정보
    publishedAt: timestamp('published_at', { withTimezone: true }).notNull(),
    publishedBy: text('published_by'),

    // 캐시 버전 (클라이언트 캐싱용)
    version: integer('version').notNull().default(1),
}, (table) => [
    // UNIQUE 제약: section + locale 조합은 유일
    unique('cms_published_cache_unique').on(table.section, table.locale),

    // 인덱스: section으로 조회 최적화
    index('cms_published_cache_section_idx').on(table.section),
]);

// 타입 추론
export type CmsPublishedCache = typeof cmsPublishedCache.$inferSelect;
export type NewCmsPublishedCache = typeof cmsPublishedCache.$inferInsert;

/**
 * 사용 예시:
 *
 * // 캐시 생성/업데이트 (UPSERT)
 * await db.insert(cmsPublishedCache)
 *   .values({
 *     section: 'home',
 *     locale: 'ko',
 *     content: {
 *       'home.hero.title': {
 *         type: 'text',
 *         content: '미래를 만드는 기업'
 *       },
 *       'home.hero.image': {
 *         type: 'image',
 *         url: '/uploads/hero.jpg',
 *         alt: 'Hero',
 *         width: 1920,
 *         height: 1080
 *       }
 *     },
 *     publishedAt: new Date(),
 *     publishedBy: 'admin@futureplay.com'
 *   })
 *   .onConflictDoUpdate({
 *     target: [cmsPublishedCache.section, cmsPublishedCache.locale],
 *     set: {
 *       content: sql`EXCLUDED.content`,
 *       publishedAt: sql`EXCLUDED.published_at`,
 *       publishedBy: sql`EXCLUDED.published_by`,
 *       version: sql`${cmsPublishedCache.version} + 1`
 *     }
 *   });
 *
 * // 캐시 조회 (초고속!)
 * const cache = await db.select()
 *   .from(cmsPublishedCache)
 *   .where(and(
 *     eq(cmsPublishedCache.section, 'home'),
 *     eq(cmsPublishedCache.locale, 'ko')
 *   ))
 *   .limit(1);
 *
 * const labels = cache[0].content; // 즉시 사용 가능!
 *
 * // 섹션의 모든 언어 캐시 조회
 * const allLocales = await db.select()
 *   .from(cmsPublishedCache)
 *   .where(eq(cmsPublishedCache.section, 'home'));
 *
 * // 오래된 캐시 감지
 * const stale = await db.select()
 *   .from(cmsPublishedCache)
 *   .where(lt(
 *     cmsPublishedCache.publishedAt,
 *     new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
 *   ));
 */